import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const AccessPolicySection = () => {
  const [policies, setPolicies] = useState({
    timeRestrictions: {
      enabled: true,
      startTime: '06:00',
      endTime: '22:00',
      weekendsEnabled: false
    },
    securityThresholds: {
      maxFailedAttempts: 3,
      lockoutDuration: 15,
      anomalyDetection: true,
      suspiciousHours: true
    },
    behavioralAnalysis: {
      enabled: true,
      nightTimeAlert: true,
      multipleAttemptsThreshold: 2,
      timeWindow: 5
    }
  });

  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);

  const timeOptions = Array.from({ length: 24 }, (_, i) => {
    const hour = i?.toString()?.padStart(2, '0');
    return {
      value: `${hour}:00`,
      label: `${hour}:00`
    };
  });

  const handlePolicyChange = (section, field, value) => {
    setPolicies(prev => ({
      ...prev,
      [section]: {
        ...prev?.[section],
        [field]: value
      }
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    setSaving(false);
    setHasChanges(false);
    
    // Show success notification (would integrate with notification system)
    console.log('Policies saved successfully');
  };

  const handleReset = () => {
    // Reset to default values
    setPolicies({
      timeRestrictions: {
        enabled: true,
        startTime: '06:00',
        endTime: '22:00',
        weekendsEnabled: false
      },
      securityThresholds: {
        maxFailedAttempts: 3,
        lockoutDuration: 15,
        anomalyDetection: true,
        suspiciousHours: true
      },
      behavioralAnalysis: {
        enabled: true,
        nightTimeAlert: true,
        multipleAttemptsThreshold: 2,
        timeWindow: 5
      }
    });
    setHasChanges(false);
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6 security-shadow-card">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground flex items-center space-x-2">
            <Icon name="Shield" size={20} className="text-primary" />
            <span>Access Policy Configuration</span>
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            Configure time-based restrictions, security thresholds, and behavioral analysis parameters
          </p>
        </div>
        {hasChanges && (
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleReset}
              disabled={saving}
            >
              Reset
            </Button>
            <Button
              variant="default"
              size="sm"
              loading={saving}
              iconName="Save"
              iconPosition="left"
              onClick={handleSave}
            >
              Save Changes
            </Button>
          </div>
        )}
      </div>
      <div className="space-y-8">
        {/* Time Restrictions */}
        <div className="space-y-4">
          <div className="flex items-center space-x-3">
            <Checkbox
              checked={policies?.timeRestrictions?.enabled}
              onChange={(e) => handlePolicyChange('timeRestrictions', 'enabled', e?.target?.checked)}
            />
            <div>
              <h4 className="text-md font-medium text-foreground">Time-Based Access Restrictions</h4>
              <p className="text-sm text-muted-foreground">Limit access to specific time windows</p>
            </div>
          </div>
          
          {policies?.timeRestrictions?.enabled && (
            <div className="ml-8 space-y-4 p-4 bg-muted/30 rounded-lg border border-border/50">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Select
                  label="Start Time"
                  options={timeOptions}
                  value={policies?.timeRestrictions?.startTime}
                  onChange={(value) => handlePolicyChange('timeRestrictions', 'startTime', value)}
                />
                <Select
                  label="End Time"
                  options={timeOptions}
                  value={policies?.timeRestrictions?.endTime}
                  onChange={(value) => handlePolicyChange('timeRestrictions', 'endTime', value)}
                />
              </div>
              <Checkbox
                label="Enable weekend access"
                description="Allow access during Saturday and Sunday"
                checked={policies?.timeRestrictions?.weekendsEnabled}
                onChange={(e) => handlePolicyChange('timeRestrictions', 'weekendsEnabled', e?.target?.checked)}
              />
            </div>
          )}
        </div>

        {/* Security Thresholds */}
        <div className="space-y-4">
          <div>
            <h4 className="text-md font-medium text-foreground flex items-center space-x-2">
              <Icon name="AlertTriangle" size={16} className="text-warning" />
              <span>Security Thresholds</span>
            </h4>
            <p className="text-sm text-muted-foreground">Configure security limits and lockout policies</p>
          </div>
          
          <div className="space-y-4 p-4 bg-muted/30 rounded-lg border border-border/50">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Max Failed Attempts"
                type="number"
                min="1"
                max="10"
                value={policies?.securityThresholds?.maxFailedAttempts}
                onChange={(e) => handlePolicyChange('securityThresholds', 'maxFailedAttempts', parseInt(e?.target?.value))}
                description="Number of failed attempts before lockout"
              />
              <Input
                label="Lockout Duration (minutes)"
                type="number"
                min="5"
                max="60"
                value={policies?.securityThresholds?.lockoutDuration}
                onChange={(e) => handlePolicyChange('securityThresholds', 'lockoutDuration', parseInt(e?.target?.value))}
                description="How long to lock out after failed attempts"
              />
            </div>
            
            <div className="space-y-3">
              <Checkbox
                label="Enable anomaly detection"
                description="Automatically flag unusual access patterns"
                checked={policies?.securityThresholds?.anomalyDetection}
                onChange={(e) => handlePolicyChange('securityThresholds', 'anomalyDetection', e?.target?.checked)}
              />
              <Checkbox
                label="Alert on suspicious hours"
                description="Flag access attempts between 12AM-6AM"
                checked={policies?.securityThresholds?.suspiciousHours}
                onChange={(e) => handlePolicyChange('securityThresholds', 'suspiciousHours', e?.target?.checked)}
              />
            </div>
          </div>
        </div>

        {/* Behavioral Analysis */}
        <div className="space-y-4">
          <div className="flex items-center space-x-3">
            <Checkbox
              checked={policies?.behavioralAnalysis?.enabled}
              onChange={(e) => handlePolicyChange('behavioralAnalysis', 'enabled', e?.target?.checked)}
            />
            <div>
              <h4 className="text-md font-medium text-foreground">Behavioral Analysis</h4>
              <p className="text-sm text-muted-foreground">Advanced pattern recognition and anomaly detection</p>
            </div>
          </div>
          
          {policies?.behavioralAnalysis?.enabled && (
            <div className="ml-8 space-y-4 p-4 bg-muted/30 rounded-lg border border-border/50">
              <Checkbox
                label="Night-time access alerts"
                description="Generate alerts for access between midnight and 6AM"
                checked={policies?.behavioralAnalysis?.nightTimeAlert}
                onChange={(e) => handlePolicyChange('behavioralAnalysis', 'nightTimeAlert', e?.target?.checked)}
              />
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Multiple Attempts Threshold"
                  type="number"
                  min="2"
                  max="10"
                  value={policies?.behavioralAnalysis?.multipleAttemptsThreshold}
                  onChange={(e) => handlePolicyChange('behavioralAnalysis', 'multipleAttemptsThreshold', parseInt(e?.target?.value))}
                  description="Number of attempts to trigger alert"
                />
                <Input
                  label="Time Window (minutes)"
                  type="number"
                  min="1"
                  max="30"
                  value={policies?.behavioralAnalysis?.timeWindow}
                  onChange={(e) => handlePolicyChange('behavioralAnalysis', 'timeWindow', parseInt(e?.target?.value))}
                  description="Time window for multiple attempts"
                />
              </div>
            </div>
          )}
        </div>
      </div>
      {/* Policy Summary */}
      <div className="mt-8 p-4 bg-accent/10 rounded-lg border border-accent/20">
        <h5 className="text-sm font-medium text-accent mb-2 flex items-center space-x-2">
          <Icon name="Info" size={16} />
          <span>Current Policy Summary</span>
        </h5>
        <div className="text-xs text-muted-foreground space-y-1">
          <p>• Time restrictions: {policies?.timeRestrictions?.enabled ? `${policies?.timeRestrictions?.startTime} - ${policies?.timeRestrictions?.endTime}` : 'Disabled'}</p>
          <p>• Max failed attempts: {policies?.securityThresholds?.maxFailedAttempts} (lockout: {policies?.securityThresholds?.lockoutDuration}min)</p>
          <p>• Behavioral analysis: {policies?.behavioralAnalysis?.enabled ? 'Enabled' : 'Disabled'}</p>
          <p>• Anomaly detection: {policies?.securityThresholds?.anomalyDetection ? 'Active' : 'Inactive'}</p>
        </div>
      </div>
    </div>
  );
};

export default AccessPolicySection;